import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { fromJS, Map } from 'immutable';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { LoggerService } from 'i2g-ng-utils';

@Component({
  selector: 'i2g-form-master',
  templateUrl: 'form-master.component.html',
  styleUrls: ['form-master.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormMasterComponent implements OnChanges, OnDestroy {
  @Input() form: FormGroup;
  @Input() preventSubmitOnEnter: boolean;
  @Input() debounceTime = 0;

  @Output() formValidity: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() rawFormChanges: EventEmitter<Map<string, any>> = new EventEmitter<
    Map<string, any>
  >();
  @Output() formChanges: EventEmitter<Map<string, any>> = new EventEmitter<
    Map<string, any>
  >();
  @Output() validFormChanges: EventEmitter<Map<string, any>> = new EventEmitter<
    Map<string, any>
  >();
  @Output() submitForm: EventEmitter<Map<string, any>> = new EventEmitter<
    Map<string, any>
  >();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _formSub: Subscription;

  constructor(private _loggerService: LoggerService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.form && this.form) {
      this.subscribeToFormChanges();
      this.checkFormValidity();
    }
  }

  subscribeToFormChanges() {
    if (this._formSub) {
      this._formSub.unsubscribe();
    }

    this._formSub = this.form.valueChanges
      .pipe(takeUntil(this._destroyed), debounceTime(this.debounceTime))
      .subscribe((changes) => {
        if (this.form.valid) {
          this.validFormChanges.emit(fromJS(changes));
        }

        this.formChanges.emit(fromJS(changes));

        // getRawValue() includes all field values (including disabled ones)
        this.rawFormChanges.emit(fromJS(this.form.getRawValue()));

        this.checkFormValidity();
      });
  }

  checkFormValidity() {
    this.formValidity.emit(this.form.valid);
  }

  onSubmit(evt: Event) {
    if (this.form.valid && this.form.enabled) {
      this._loggerService.log('submitting form', this.form.value);

      evt.preventDefault();

      this.submitForm.emit(fromJS(this.form.value));
    } else {
      this._loggerService.log('tried to submit form, but form is not valid');
    }
  }

  ngOnDestroy() {
    if (this._formSub) {
      this._formSub.unsubscribe();
    }

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
